import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';

import {
  FormBuilder,
  FormGroup,
  AbstractControl,
  ValidationErrors,
  ValidatorFn,
  Validators,
  FormControl,
  FormArray,
  FormControlStatus,
} from '@angular/forms';

import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';

import { translate, TranslocoService } from '@jsverse/transloco';
import { Subject } from 'rxjs';
import { debounceTime, filter, takeUntil } from 'rxjs/operators';
import { DropdownObject, ErrorObject } from '@inmoment/the-kitchen';

import { AppSharedModule } from 'app/app-shared.module';
import { Constants, FieldConstants } from 'app/constants/constants';
import { AuthService } from 'app/providers/bridge-api/auth.service';
import { RealtimeMessage } from 'app/providers/realtime.service';
import { GroupService } from 'app/providers/semantria-api/group.service';
import {
  AccountDetails,
  SemantriaAccountService,
} from 'app/providers/semantria-api/semantria-account.service';
import {
  Field,
  FieldDetails,
  FieldRedactionBehavior,
  FieldService,
  isReservedPrefixValidator,
} from 'app/providers/storage-api/field.service';
import { AlertService } from 'app/services/alert.service';
import { DialogService } from 'app/services/dialog.service';
import {
  injectComponentTelemetryService,
  EnableComponentTelemetry,
  EnableClassTelemetry,
} from 'app/services/otel.service';
import { SystemService } from 'app/services/system.service';
import { jsonValidator, noWhitespaceValidator } from 'app/utils/customValidators';
import { ErrorObjectUtils } from 'app/utils/error-object.utils';
import { Utils } from 'app/utils/utils';
import { conditionalValidator, ExtractFormControl } from 'app/utils/validator.utils';
import { SimpleDialogButtonColor } from '../../../components/simple-dialog.component';
import { FieldAliasSelectorComponent } from '../field-alias-selector/field-alias-selector.component';
import {
  FieldBucketsComponent,
  FieldBucketsFormValue,
  FieldBucketType,
} from '../field-buckets/field-buckets.component';
import {
  FieldCreateModalComponent,
  FieldResult,
} from '../field-create-modal/field-create-modal.component';

interface FieldForm {
  label: FormControl<string>;
  type: FormControl<string>;
  subType: FormControl<string>;
  rating: FormControl<boolean>;
  filterable: FormControl<boolean>;
  identityField: FormControl<boolean>;
  weight: FormControl<boolean>;
  description: FormControl<string>;
  redacted: FormControl<boolean>;
  redactionBehavior: FormControl<FieldRedactionBehavior>;
  calcType: FormControl<string>;
  calcDef: FormControl<string>;
  aliasedFields: FormArray<FormControl<string>>;
  locked: FormControl<boolean>;
}

type FieldFormValue = ExtractFormControl<FieldForm>;

@EnableClassTelemetry()
@Component({
  selector: 'app-field-view',
  templateUrl: './field-view.component.html',
  styleUrls: ['./field-view.component.scss'],
  imports: [
    AppSharedModule,
    FieldAliasSelectorComponent,
    FieldBucketsComponent,
    FieldCreateModalComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FieldViewComponent implements OnInit, OnDestroy {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();

  isLoading = true;
  isSaving = false;
  isUnsaved = false;

  readonly isReadOnly: boolean;
  private readonly _isAccountAdmin: boolean;

  private readonly _destroy$ = new Subject<void>();

  fieldId: string;
  groupId: string;
  field: Field;

  fieldForm: FormGroup<FieldForm>;
  originalFormValue: FieldFormValue;
  fieldLabelStatus: ErrorObject;
  fieldDescriptionStatus: ErrorObject;
  calcDefStatus: ErrorObject = ErrorObjectUtils.create();

  fieldTypes: DropdownObject[];
  calcTypes: DropdownObject[];
  calcTypesFieldType: ReadonlyMap<string, string> = new Map<string, string>([
    ['math', 'double'],
    ['age', 'keyword'],
    ['score', 'double'],
    ['average', 'double'],
    ['repercentage', 'double'],
    ['rescore', 'double'],
    ['weighted-index', 'double'],
    ['count', 'long'],
    ['content_channel', 'keyword'],
    ['binary', 'double'],
  ]);

  standardTypes = [
    { title: '', id: 'keyword' },
    { title: '', id: 'long' },
    { title: '', id: 'date' },
    { title: '', id: 'double' },
    { title: '', id: 'calculated' },
  ];
  aliasType = [{ title: '', id: 'alias' }];

  fieldRedactionBehaviors = [
    { title: '', id: FieldRedactionBehavior.HIDE },
    { title: '', id: FieldRedactionBehavior.MASK },
  ];

  allFields: Field[] = [];
  labelMaxLength: number;
  descriptionMaxLength: number;

  aliasedFieldTableData: MatTableDataSource<any>;
  selectedAliasedFields: string[];

  activeUseTableColumns = ['projectName', 'collectionName', 'documentCount'];
  activeUseTableData: MatTableDataSource<FieldDetails>;
  aliasesTableColumns = ['fieldLabel', 'isFilterable', 'isPII', 'fieldName'];
  aliasesTableData: MatTableDataSource<Field>;
  translations: any;
  fieldDeleted = false;
  showNewMetadataFieldModal = false;

  readonly PROJECT_NAME_CHARACTER_LIMIT = 40;
  readonly toggleControlNames = ['rating', 'weight', 'redacted', 'filterable', 'identityField'];
  readonly GLOBAL = FieldConstants.GLOBAL;
  readonlyFields = {};
  showBuckets = false;
  expectedType = signal(null);
  bucketForm: FieldBucketsFormValue = { valid: true, value: [] };

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private fieldService: FieldService,
    private alertService: AlertService,
    private dialogService: DialogService,
    private fb: FormBuilder,
    private accountService: SemantriaAccountService,
    private translocoService: TranslocoService,
    private systemService: SystemService,
    private groupService: GroupService,
    private cdr: ChangeDetectorRef,
    authService: AuthService
  ) {
    this.isReadOnly = authService.isReadOnly;
    this._isAccountAdmin = authService.isAccountAdmin;
  }

  ngOnInit(): void {
    this.groupId = this.groupService.getSelectedGroupId(true);

    this.translocoService
      .selectTranslateObject('field-view')
      .pipe(takeUntil(this._destroy$))
      .subscribe((translatedValue) => {
        this.setTranslations(translatedValue);
        this.cdr.markForCheck();
      });

    this.accountService.sub.takeUntil(this._destroy$).subscribe((accountDetails) => {
      this.setAccountDetails(accountDetails);
      this.cdr.markForCheck();
    });

    this.buildForm();

    this.route.paramMap.pipe(takeUntil(this._destroy$)).subscribe((params) => {
      this.fieldId = params.get('fieldId');
      this.onReloadField();
    });

    this.fieldService.realtimeEvent$
      .pipe(
        takeUntil(this._destroy$),
        filter((data) => data.object.fieldName === this.field.fieldName)
      )
      .subscribe((data: RealtimeMessage<Field>) => {
        if (data.verb === 'UPDATE') {
          this.field = { ...data.object, details: this.field.details };
          this.isUnsaved = true;
        } else if (data.verb === 'DELETE') {
          this.fieldDeleted = true;
        }
        this.cdr.markForCheck();
      });
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  onAliasSelectionChange(value: { includedFields: string[]; containsPII: boolean }) {
    this.fieldForm.patchValue({
      redacted: value.containsPII,
    });

    this.setAliasedFieldsOnForm(value.includedFields);
  }

  async onReloadField(): Promise<void> {
    this.isLoading = true;
    this.cdr.markForCheck();
    this.fieldDeleted = false;

    try {
      this.allFields = (await this.fieldService.getFields()).items;
      this.fieldService.sortFields(this.allFields);

      this.field = await this.fieldService.getFieldById(this.fieldId);

      this.loadField();
      this.setFormFieldStates();

      this.createActiveUseTable();
      this.createAliasesTable();
    } catch (e) {
      this.alertService.showErrorToast(
        this.translocoService.translate('field-view.failed-to-load'),
        e
      );
    } finally {
      this.isLoading = false;
      this.cdr.markForCheck();
    }
  }

  async onDeleteField(): Promise<void> {
    this.dialogService
      .showSimpleDialog({
        title: this.translocoService.translate('field-view.delete-popup.title'),
        content: this.translocoService.translate('field-view.delete-popup.content'),
        extraContent: this.field.label,
        confirmTitle: this.translocoService.translate('field-view.delete-popup.confirm-title'),
        cancelTitle: this.translocoService.translate('field-view.delete-popup.cancel'),
        confirmColor: SimpleDialogButtonColor.red,
      })
      .afterClosed()
      .subscribe(async (result) => {
        if (result) {
          this.isLoading = true;
          this.cdr.markForCheck();
          try {
            await this.fieldService.deleteField(this.fieldId);
            this.alertService.showToast(
              this.translocoService.translate('field-view.successfully-delete', {
                field: this.field.description,
              })
            );
          } catch (error) {
            this.alertService.showErrorToast(
              this.translocoService.translate('field-view.failed-to-delete', {
                field: this.field.description,
              }),
              error
            );
          } finally {
            this.cdr.markForCheck();
            this.isLoading = false;
          }
        }
      });
  }

  onGoToCollection(fieldDetails: FieldDetails): void {
    const url = `/groups/${fieldDetails.groupId.split('-')[0]}/projects/${fieldDetails.projectId}`;
    this.router.navigateByUrl(url);
  }

  onCopyDetail(text: string) {
    this.systemService.copyToClipboard(text);
  }

  async onSave(): Promise<boolean> {
    this.isLoading = true;

    const formValue = this.fieldForm.getRawValue();

    try {
      if (formValue.locked !== this.field.locked) {
        //Field must be updated before locking
        if (formValue.locked) {
          this.field = await this.updateField(formValue);
          await this.fieldService.lockField(this.field.id);
        } else {
          //Field must be unlocked before updating
          await this.fieldService.unlockField(this.field.id);
          this.field = await this.updateField(formValue);
        }
      } else {
        this.field = await this.updateField(formValue);
      }

      this.originalFormValue = formValue;
      this.bucketForm = {
        ...this.bucketForm,
        value: this.field.buckets,
      };
      this.setSelectedAliasedFields();
      this.isUnsaved = false;
      this.alertService.showToast(this.translocoService.translate('field-view.changes-saved'));
    } catch (error) {
      if (this.isCalculatedField && (error as HttpErrorResponse).status === 400) {
        this.alertService.showErrorToast(
          this.translocoService.translate('field-view.invalid-json'),
          error
        );
      } else {
        this.alertService.showErrorToast(
          this.translocoService.translate('field-view.failed-to-update'),
          error
        );
      }

      this.isLoading = false;
      this.cdr.markForCheck();
      return false;
    }

    this.isLoading = false;
    this.cdr.markForCheck();
    return true;
  }

  private async updateField(formValue) {
    const buckets = !this.showBuckets ? [] : this.bucketForm.value;
    const isAlias = formValue.type === 'alias';
    const type = isAlias ? formValue.subType : formValue.type;
    const field = await this.fieldService.updateField(
      this.field.id,
      formValue.label,
      formValue.description,
      type,
      {
        rating: formValue.rating,
        weight: formValue.weight,
        redacted: formValue.redacted,
        redactionBehavior: formValue.redactionBehavior,
        filterable: formValue.filterable,
        calculationType: formValue.calcType,
        calculationDefinition: JSON.parse(formValue.calcDef),
      },
      isAlias,
      formValue.aliasedFields,
      buckets,
      formValue.identityField
    );
    field.details = this.field.details;
    return field;
  }

  onRevert(): void {
    this.loadField();
    this.setFormFieldStates();
    this.field.buckets = [...(this.field.buckets ?? [])];
  }

  get isCalculatedField(): boolean {
    return this.fieldForm.controls.type.value === 'calculated';
  }

  get shouldShowRating(): boolean {
    return FieldService.hasNumerics(
      this.fieldForm.controls.type.value,
      this.calcTypesFieldType.get(this.fieldForm.controls.calcType.value),
      this.fieldForm.controls.subType.value
    );
  }

  get shouldShowUseAsIdField(): boolean {
    return this.fieldForm.controls.type.value === 'keyword';
  }

  get shouldShowRedacted(): boolean {
    return this._isAccountAdmin && !this.isCalculatedField;
  }

  get shouldShowRedactionBehavior(): boolean {
    return this._isAccountAdmin && this.fieldForm.controls.redacted.value;
  }

  get shouldShowWeight(): boolean {
    return (
      this.fieldForm.controls.type.value === 'double' ||
      this.fieldForm.controls.subType.value === 'double'
    );
  }

  get shouldShowSaveBar(): boolean {
    return this.isUnsaved && !this.isLoading && !this.isSaving && !this.fieldDeleted;
  }

  get canEditRedactionBehavior(): boolean {
    return ['string', 'keyword', 'nonindexed'].includes(this.fieldForm.controls.type.value);
  }

  get deleteTooltip() {
    if (this.field.documentCount) {
      if (this.field.alias) {
        return this.translations['must-delete-alias-text'];
      }
      return this.translations['must-delete-text'];
    } else if (this.field.locked) {
      return this.translations['cannot-delete-locked'];
    } else if (this.aliasesTableData.data?.length) {
      return this.translations['must-delete-aliases-text'];
    }
    return this.translations['delete-field'];
  }

  get redactedFieldTooltip() {
    if (this.field.alias) {
      if (this.field.metadata?.redacted) {
        return this.translations['fields-contain-pii'];
      }
      return this.translations['no-fields-contain-pii'];
    }

    if (this.hasDetails && this.field.metadata?.redacted) {
      return this.translations['cannot-change-pii-field'];
    }
  }

  get ratingTooltip(): string {
    return this.fieldForm.controls.weight.value && !this.field.alias
      ? this.translations['cannot-change-rating']
      : '';
  }

  get weightTooltip(): string {
    return this.fieldForm.controls.rating.value && !this.field.alias
      ? this.translations['cannot-change-weight']
      : '';
  }

  get identityFieldDisabledTooltip(): string | undefined {
    if (this.field.journeyGroupUsages?.length > 0 && this.field.identityField) {
      return this.translations['cannot-change-identity-field'];
    }
    return undefined;
  }

  get hasDetails(): boolean {
    return !!this.field.details?.length;
  }

  get selectedSubType(): string {
    return this.fieldForm.controls.subType.value;
  }

  get isLocked(): boolean {
    return this.fieldForm.controls.locked.value;
  }

  private validateInput(
    formStatus: string,
    errors: ValidationErrors,
    input: ErrorObject,
    inputField: string
  ) {
    if (formStatus === 'INVALID' && errors) {
      switch (Object.keys(errors)[0]) {
        case 'required':
        case 'whitespace':
          ErrorObjectUtils.setError(
            input,
            this.translocoService.translate('field-view.name-required')
          );
          break;

        case 'unique':
          ErrorObjectUtils.setError(
            input,
            this.translocoService.translate('field-view.unique-name')
          );
          break;
        case 'pattern':
          ErrorObjectUtils.setError(
            input,
            this.translocoService.translate('field-view.invalid-name')
          );
          break;
        case 'maxlength':
          const maxLengthMessage =
            inputField === 'label'
              ? 'field-view.max-length-name'
              : 'field-view.max-length-long-name';
          ErrorObjectUtils.setError(
            input,
            this.translocoService.translate(maxLengthMessage, {
              maxlength: errors.maxlength.requiredLength,
            })
          );
          break;
        case 'reserved':
          ErrorObjectUtils.setError(
            input,
            this.translocoService.translate('field-view.reserved-prefix')
          );
          break;
      }
    } else {
      ErrorObjectUtils.setNoError(input);
    }
  }

  onTextPasted($event: ClipboardEvent, control, length) {
    $event.preventDefault();
    const clipboardData = $event.clipboardData;
    let pastedText = clipboardData.getData('text');
    if (pastedText.trim().length > length) {
      pastedText = pastedText.trim().substring(0, length);
    }
    control.setValue(pastedText);
  }

  private labelValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      for (const field of this.allFields) {
        if (field.id === this.fieldId) {
          continue;
        }
        const normalizedName = control.value
          .toLowerCase()
          .replace(FieldService.NORMALIZED_NAME_RE, '');
        if (normalizedName === field.normalizedName) {
          return { unique: true };
        }
      }
      return null;
    };
  }

  private checkUnsavedChanges(): void {
    this.isUnsaved =
      !Utils.deepEquals(this.originalFormValue, this.fieldForm.getRawValue()) ||
      this.fieldBucketsChanged();
    this.cdr.markForCheck();
  }

  private fieldBucketsChanged(): boolean {
    if (
      (!this.field.buckets && !this.bucketForm) ||
      (!this.field.buckets?.length && !this.bucketForm?.value?.length)
    ) {
      return false;
    }
    if (this.field.buckets?.length !== this.bucketForm?.value?.length) {
      return true;
    }
    let hasChanges = false;
    for (let i = 0; i < this.field.buckets.length; i++) {
      hasChanges = !Utils.deepEquals(this.field.buckets[i], this.bucketForm.value[i]);
    }
    return hasChanges;
  }

  private setTranslations(translatedValue): void {
    this.translations = translatedValue;

    this.fieldRedactionBehaviors.forEach((behavior) => {
      switch (behavior.id) {
        case FieldRedactionBehavior.HIDE:
          behavior.title = this.translations['redaction-behaviors']['hide'];
          break;
        case FieldRedactionBehavior.MASK:
          behavior.title = this.translations['redaction-behaviors']['mask'];
          break;
      }
    });

    this.standardTypes.forEach((fieldType) => {
      fieldType.title = this.translations['field-types'][fieldType.id] || fieldType.id;
    });

    this.aliasType[0].title = this.translations['field-types']['alias'];

    this.calcTypes = [
      { title: this.translations['calculation-types']['score'], id: 'score' },
      { title: this.translations['calculation-types']['math'], id: 'math' },
      { title: this.translations['calculation-types']['average'], id: 'average' },
      { title: this.translations['calculation-types']['repercentage'], id: 'repercentage' },
      { title: this.translations['calculation-types']['rescore'], id: 'rescore' },
      { title: this.translations['calculation-types']['weighted-index'], id: 'weighted-index' },
      { title: this.translations['calculation-types']['age'], id: 'age' },
      { title: this.translations['calculation-types']['binary'], id: 'binary' },
    ];
  }

  private setAccountDetails(accountDetails: AccountDetails): void {
    this.labelMaxLength =
      accountDetails?.limits?.SPOTLIGHT_MAX_SHORT_FIELD_LENGTH ??
      Constants.SPOTLIGHT_MAX_SHORT_FIELD_DEFAULT_LENGTH;

    this.descriptionMaxLength =
      accountDetails?.limits?.SPOTLIGHT_MAX_LONG_FIELD_LENGTH ??
      Constants.SPOTLIGHT_MAX_LONG_FIELD_DEFAULT_LENGTH;
  }

  private buildForm(): void {
    this.fieldForm = this.fb.group<FieldForm>({
      label: new FormControl(''),
      type: new FormControl('', Validators.required),
      subType: new FormControl(''),
      rating: new FormControl(false),
      filterable: new FormControl(false),
      identityField: new FormControl(false),
      weight: new FormControl(false),
      description: new FormControl('', [
        Validators.required,
        Validators.maxLength(this.descriptionMaxLength),
        noWhitespaceValidator,
      ]),
      redacted: new FormControl(false),
      redactionBehavior: new FormControl(FieldRedactionBehavior.HIDE),
      locked: new FormControl(false),
      calcType: new FormControl(
        null,
        conditionalValidator(() => this.isCalculatedField, Validators.required)
      ),
      calcDef: new FormControl(
        null,
        conditionalValidator(
          () => this.isCalculatedField,
          Validators.compose([Validators.required, jsonValidator()])
        )
      ),
      aliasedFields: new FormArray([]),
    });

    this.fieldForm.statusChanges.subscribe((status) =>
      this.createErrorObjectAndValidateInput(status)
    );

    this.fieldForm.controls.rating.valueChanges
      .pipe(
        takeUntil(this._destroy$),
        filter((_) => !this.isReadOnly && !this.field.alias && !this.field.locked)
      )
      .subscribe((rating) => {
        if (rating) {
          this.fieldForm.controls.weight.disable({ emitEvent: false });
        } else {
          this.fieldForm.controls.weight.enable({ emitEvent: false });
        }
      });

    this.fieldForm.controls.weight.valueChanges
      .pipe(
        takeUntil(this._destroy$),
        filter(
          (_) =>
            !this.isReadOnly &&
            !this.field.alias &&
            !this.field.locked &&
            this.field.groupId !== this.GLOBAL
        )
      )
      .subscribe((weight) => {
        if (weight) {
          this.fieldForm.controls.rating.disable({ emitEvent: false });
        } else {
          this.fieldForm.controls.rating.enable({ emitEvent: false });
        }
      });

    this.fieldForm.controls.type.valueChanges.pipe(takeUntil(this._destroy$)).subscribe((type) => {
      if (type === 'keyword' || type === 'date') {
        this.fieldForm.patchValue({
          rating: false,
          weight: false,
          filterable: false,
          redactionBehavior: FieldRedactionBehavior.HIDE,
        });
      }

      if (type !== 'calculated') {
        this.fieldForm.patchValue({
          calcType: null,
          calcDef: null,
        });
      }

      if (type !== 'keyword') {
        this.fieldForm.patchValue({ identityField: false });
      }

      let expectedTypeToUse: FieldBucketType | null = null;
      if (type === 'long') {
        expectedTypeToUse = FieldBucketType.INTEGER;
      } else if (type === 'double') {
        expectedTypeToUse = FieldBucketType.FLOAT;
      }
      this.expectedType.set(expectedTypeToUse);
      this.showBuckets = expectedTypeToUse !== null;
    });

    this.fieldForm.controls.calcType.valueChanges
      .pipe(takeUntil(this._destroy$))
      .subscribe((calcType) => {
        if (calcType === 'keyword' || calcType === 'date') {
          this.fieldForm.patchValue({
            rating: false,
          });
        }
      });

    this.fieldForm.controls.locked.valueChanges
      .pipe(
        takeUntil(this._destroy$),
        filter((_) => !this.isReadOnly)
      )
      .subscribe((isLocked) => {
        if (isLocked) {
          this.setFormFieldsReadonly();
        } else {
          this.setFormFieldsReadonly(false);
          this.fieldForm.enable({ emitEvent: false });
          // Patch all except locked field
          const { locked, ...prevFormValue } = this.fieldForm.value;
          this.fieldForm.patchValue(prevFormValue);
          this.setFormFieldStates();
        }
      });

    this.fieldForm.valueChanges.pipe(takeUntil(this._destroy$), debounceTime(100)).subscribe(() => {
      this.checkUnsavedChanges();
    });

    this.fieldForm.controls.calcDef.statusChanges
      .pipe(takeUntil(this._destroy$))
      .subscribe((status) => {
        if (status !== 'INVALID' || !this.fieldForm.controls.calcDef.errors) {
          ErrorObjectUtils.setNoError(this.calcDefStatus);
          return;
        }

        ErrorObjectUtils.setError(this.calcDefStatus, translate('field-view.invalid-json'));
      });
  }

  private createErrorObjectAndValidateInput(status: FormControlStatus): void {
    this.fieldLabelStatus = ErrorObjectUtils.create();
    this.fieldDescriptionStatus = ErrorObjectUtils.create();
    const labelError = this.fieldForm.controls.label.errors;
    const descriptionError = this.fieldForm.controls.description.errors;
    this.validateInput(status, labelError, this.fieldLabelStatus, 'label');
    this.validateInput(status, descriptionError, this.fieldDescriptionStatus, 'description');
  }

  private loadField(): void {
    this.bucketForm = {
      valid: true,
      value: (this.field?.buckets ?? []).map((b) => b),
    };
    this.fieldForm.patchValue({
      label: this.field.label,
      description: this.field.description,
      type: this.field.fieldType,
      rating: this.field.metadata?.rating === true,
      filterable: this.field.metadata?.filterable !== false,
      identityField: this.field.identityField === true,
      weight: this.field.metadata?.weight === true,
      redacted: this.field.metadata?.redacted === true,
      redactionBehavior:
        this.field.metadata?.redactionBehavior !== undefined
          ? this.field.metadata?.redactionBehavior
          : FieldRedactionBehavior.HIDE,
      locked: this.field.locked === true,
    });

    const labelPattern =
      this.field.groupId === this.GLOBAL
        ? FieldService.GLOBAL_FIELD_VALID_RE
        : FieldService.FIELD_VALID_RE;

    this.fieldForm.controls.label.setValidators([
      Validators.required,
      Validators.pattern(labelPattern),
      Validators.maxLength(this.labelMaxLength),
      this.labelValidator(),
      noWhitespaceValidator,
      isReservedPrefixValidator,
    ]);
    this.fieldForm.controls.label.updateValueAndValidity({ emitEvent: false });

    this.fieldForm.controls.aliasedFields.clear();

    if (this.field.alias) {
      this.fieldTypes = this.aliasType;

      this.fieldForm.patchValue({
        type: 'alias',
        subType: this.field.fieldType,
      });

      this.setAliasedFieldsOnForm(this.field.aliasedFields);

      this.setSelectedAliasedFields();

      for (const includedField of this.field.aliasedFields) {
        const field = this.allFields.find((f) => f.fieldName === includedField);
        if (field?.metadata?.redacted) {
          this.fieldForm.patchValue({
            redacted: true,
          });

          break;
        }
      }
    } else {
      this.fieldTypes = this.standardTypes;
      this.fieldForm.controls.subType.reset();
    }

    if (this.isCalculatedField) {
      this.fieldForm.patchValue({
        calcType: this.field.metadata.calculationType,
        calcDef: JSON.stringify(this.field.metadata.calculationDefinition),
      });
    }

    if (this.isReadOnly) {
      this.fieldForm.disable();
    }

    this.originalFormValue = this.fieldForm.getRawValue();
  }

  private setFormFieldStates() {
    if (this.field.locked && this.isLocked && !this.isReadOnly) {
      this.setFormFieldsReadonly();
      return;
    }

    if (this.field.alias) {
      this.fieldForm.controls.rating.disable({ emitEvent: false });
      this.fieldForm.controls.weight.disable({ emitEvent: false });
      this.fieldForm.controls.redacted.disable({ emitEvent: false });
    } else {
      if (this.hasDetails && this.field.metadata?.redacted === true) {
        this.fieldForm.controls.redacted.disable({ emitEvent: false });
      } else {
        this.fieldForm.controls.redacted.enable({ emitEvent: false });
      }
    }
    if (this.isCalculatedField) {
      this.readonlyFields['type'] = true;
    }
    if (this.field.groupId === this.GLOBAL) {
      this.fieldForm.controls.rating.disable({ emitEvent: false });
    }
    if (this.field.journeyGroupUsages?.length > 0 && this.field.identityField === true) {
      this.fieldForm.controls.identityField.disable({ emitEvent: false });
    }
  }

  private setAliasedFieldsOnForm(aliasedFields: string[]): void {
    this.fieldForm.controls.aliasedFields.clear();

    aliasedFields.forEach((f) => this.fieldForm.controls.aliasedFields.push(new FormControl(f)));
  }

  private createActiveUseTable(): void {
    let fieldTableProjectIds = [];

    this.field.details.sort((a, b) => (a.documentCount > b.documentCount ? -1 : 1));
    fieldTableProjectIds = this.field.details.map((item) => item.projectId);

    const projectIdsSet = [...new Set(fieldTableProjectIds)];
    const activeUseTableData = Array(projectIdsSet.length);

    for (let i = 0; i < projectIdsSet.length; i++) {
      activeUseTableData[i] = this.field.details.filter(
        (item) => item.projectId === projectIdsSet[i]
      );
    }

    activeUseTableData.sort((a, b) => a[0].projectName?.localeCompare(b[0].projectName));
    this.activeUseTableData = new MatTableDataSource(activeUseTableData);
  }

  private createAliasesTable(): void {
    const aliasesData = this.allFields
      .filter((f) => f.alias)
      .filter((af) => af.aliasedFields.includes(this.field.fieldName));
    this.aliasesTableData = new MatTableDataSource(aliasesData);
  }

  private setSelectedAliasedFields(): void {
    if (!this.field.aliasedFields) {
      return;
    }

    this.selectedAliasedFields = [...this.field.aliasedFields];
  }

  private setFormFieldsReadonly(readonly = true) {
    Object.entries(this.fieldForm.controls).forEach(
      ([name, control]: [keyof FieldFormValue, any]) => {
        this.readonlyFields[name] = name !== 'locked' && readonly;
        if (this.toggleControlNames.includes(name)) {
          readonly ? control.disable({ emitEvent: false }) : control.enable({ emitEvent: false });
        }
      }
    );
  }

  public onShowNewMetadataFieldModal() {
    this.showNewMetadataFieldModal = true;
  }

  public async onNewMetadataFieldModalClosed(result: FieldResult) {
    this.showNewMetadataFieldModal = false;
    const res = await this.fieldService.createMetadataField(result);
    if (res) {
      this.allFields = (await this.fieldService.getFields()).items;
      this.createAliasesTable();
    }
    this.cdr.markForCheck();
  }

  onFieldBucketChange(fieldBucketsFormValue: FieldBucketsFormValue) {
    this.bucketForm = fieldBucketsFormValue;
    this.checkUnsavedChanges();
  }
}
